import React, { useState, useEffect } from 'react';
import { useVoice } from '../VoiceContext';
import {
  getAllLanguages,
  getThemesByLanguage,
  getFiltersByUser,
  getWordsByTheme,
  getWordsByFilter,
  getWordsByThemeAndFilter,
  getReviewWords,
  getReviewWordIds,
  addWordToReview,
  removeWordFromReview,
} from '../api';

import {  
  Box, Typography, Paper, CircularProgress, Alert, Chip, Stack,
  FormControl, InputLabel, Select, MenuItem, Button, IconButton, Tooltip,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


const shuffleArray = (array) => {
  return [...array].sort(() => Math.random() - 0.5);
};

const CardsPage = () => {
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [activeSpeech, setActiveSpeech] = useState(null);
  const { voices, loading: voicesLoading } = useVoice();
  const [feedback, setFeedback] = useState('');
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState({
    languages: false, themes: false, filters: false, words: false
  });
  const [error, setError] = useState(null);
  const [inverted, setInverted] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const [mistakes, setMistakes] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewWordIds, setReviewWordIds] = useState(new Set());

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(prev => ({ ...prev, languages: true, filters: true }));
        const [langs, userFilters] = await Promise.all([
          getAllLanguages(),
          getFiltersByUser()
        ]);
        setLanguages(langs);
        setFilters(userFilters);
        try {
          const ids = await getReviewWordIds();
          setReviewWordIds(new Set(ids));
        } catch (reviewErr) {
          console.error('Ошибка загрузки списка повторения', reviewErr);
        }
        if (langs.length > 0) setSelectedLanguage(langs[0].id);
      } catch (err) {
        setError('Ошибка загрузки данных');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, languages: false, filters: false }));
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    const loadThemes = async () => {
      if (!selectedLanguage) return;
      try {
        setLoading(prev => ({ ...prev, themes: true }));
        const themeData = await getThemesByLanguage(selectedLanguage);
        themeData.sort((a, b) => {
          const numA = parseInt(a.name.match(/^\d+/)?.[0] || 0, 10);
          const numB = parseInt(b.name.match(/^\d+/)?.[0] || 0, 10);
          if (numA !== numB) return numA - numB;
          return a.name.localeCompare(b.name);
        });
        setThemes(themeData);
        setSelectedTheme(null);
        setWords([]);
      } catch (err) {
        setError('Ошибка загрузки тем');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, themes: false }));
      }
    };
    loadThemes();
  }, [selectedLanguage]);

  useEffect(() => {
    const loadWords = async () => {
      if (!reviewMode && !selectedTheme && !selectedFilter) {
        setWords([]);
        return;
      }
      try {
        setLoading(prev => ({ ...prev, words: true }));
        let data = [];
        if (reviewMode) {
          data = await getReviewWords();
        } else if (selectedTheme && selectedFilter) {
          data = await getWordsByThemeAndFilter(selectedTheme, selectedFilter);
        } else if (selectedTheme) {
          data = await getWordsByTheme(selectedTheme);
        } else if (selectedFilter) {
          data = await getWordsByFilter(selectedFilter);
        }
        console.log('Загруженные слова:', data);
        setWords(shuffleArray(data));
        setCurrentIndex(0);
        setFinished(false);
        setFeedback('');
        setShowOptions(false);
      } catch (err) {
        setError('Ошибка загрузки слов');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, words: false }));
      }
    };
    loadWords();
  }, [selectedTheme, selectedFilter, reviewMode]);

  useEffect(() => {
    if (words.length === 0 || currentIndex >= words.length) {
      setOptions([]);
      return;
    }

    const currentWord = words[currentIndex];
    console.log('Текущее слово:', currentWord);

    const otherTranslations = shuffleArray(
      words
        .filter((_, i) => i !== currentIndex)
        .map(w => inverted ? w.russian_word : w.foreign_word)
    ).slice(0, 3);

    const optionPool = shuffleArray([
      inverted ? currentWord.russian_word : currentWord.foreign_word,
      ...otherTranslations,
    ]);
    setOptions(optionPool);
    // Убрали автоматическое произношение слова при загрузке
  }, [currentIndex, words, inverted]);

  const speakWord = (word) => {
    if (!word) return;
    if (activeSpeech) {
      window.speechSynthesis.cancel();
    }
    if (voicesLoading || voices.length === 0) {
        console.warn('Speech synthesis: No voices available or still loading. Cannot speak.');
        return;
    }
    const utterance = new SpeechSynthesisUtterance(word);
    const voiceName = localStorage.getItem('defaultItalianVoice');
    const selectedVoice = voiceName
      ? voices.find(v => v.name === voiceName)
      : null;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    setActiveSpeech(utterance);
    utterance.onend = () => setActiveSpeech(null);
    window.speechSynthesis.speak(utterance);
  };

  const handleAnswer = (answer) => {
    const correct = inverted ? words[currentIndex].russian_word : words[currentIndex].foreign_word;
    if (answer === correct) {
      setFeedback('Верно!');
      if (currentIndex + 1 >= words.length) {
        setCurrentIndex(prev => prev + 1);
        setTimeout(() => {
          setFinished(true);
        }, 800);
      } else {
        setTimeout(() => {
          setCurrentIndex(prev => prev + 1);
          setFeedback('');
          setShowOptions(false);
        }, 800);
      }
    } else {
      setFeedback('Неверно, попробуйте снова.');
      setMistakes(prev => prev + 1);
    }
  };

  const clearTheme = () => setSelectedTheme(null);
  const clearFilter = () => setSelectedFilter(null);
  const toggleInverted = () => {
    setInverted(!inverted);
    setCurrentIndex(0); // Сбрасываем текущий индекс при инвертировании
  };

  const handleRepeat = () => {
    setWords(shuffleArray(words));
    setCurrentIndex(0);
    setFinished(false);
    setFeedback('');
    setShowOptions(false);
    setMistakes(0);
  };

  const currentWord = words[currentIndex];
  const isCurrentWordInReview = Boolean(currentWord && reviewWordIds.has(currentWord.id));

  const handleAddCurrentWordToReview = async () => {
    if (!currentWord) return;
    try {
      await addWordToReview(currentWord.id);
      setReviewWordIds(prev => new Set(prev).add(currentWord.id));
    } catch (err) {
      setError('Не удалось добавить слово в повторение');
      console.error(err);
    }
  };

  const handleRemoveCurrentWordFromReview = async () => {
    if (!currentWord) return;
    try {
      await removeWordFromReview(currentWord.id);
      setReviewWordIds(prev => {
        const next = new Set(prev);
        next.delete(currentWord.id);
        return next;
      });

      if (reviewMode) {
        setWords(prev => prev.filter(w => w.id !== currentWord.id));
        setCurrentIndex(prev => Math.max(0, prev - 1));
        setFeedback('');
      }
    } catch (err) {
      setError('Не удалось убрать слово из повторения');
      console.error(err);
    }
  };

  const toggleReviewMode = () => {
    setReviewMode(prev => !prev);
    setCurrentIndex(0);
    setFinished(false);
    setFeedback('');
    setShowOptions(false);
  };

  const handleLanguageChange = (value) => {
    if (reviewMode) setReviewMode(false);
    setSelectedLanguage(value);
  };

  const handleThemeChange = (value) => {
    if (reviewMode) setReviewMode(false);
    setSelectedTheme(value);
  };

  const handleFilterChange = (value) => {
    if (reviewMode) setReviewMode(false);
    setSelectedFilter(value);
  };

  return (
    <Box sx={{ p: 3 }}>
        <Typography 
        variant="h4" 
        gutterBottom
        sx={{
            display: { xs: 'none', sm: 'block' } // Скрываем на xs, показываем на sm и выше
        }}
        >
        Тренировка
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Accordion sx={{ mb: 3 }} expanded={accordionExpanded} onChange={() => setAccordionExpanded(!accordionExpanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="filters-panel-content"
          id="filters-panel-header"
        >
          <Typography>Фильтры</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
            <FormControl sx={{ minWidth: 200, flex: 1 }} disabled={loading.languages}>
              <InputLabel>Язык</InputLabel>
              <Select
                value={selectedLanguage || ''}
                onChange={(e) => handleLanguageChange(e.target.value)}
                label="Язык"
              >
                {languages.map(lang => (
                  <MenuItem key={lang.id} value={lang.id}>{lang.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200, flex: 1 }} disabled={loading.themes}>
              <InputLabel>Тема</InputLabel>
              <Select
                value={selectedTheme || ''}
                onChange={(e) => handleThemeChange(e.target.value)}
                label="Тема"
              >
                {themes.map(theme => (
                  <MenuItem key={theme.id} value={theme.id}>{theme.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200, flex: 1 }} disabled={loading.filters}>
              <InputLabel>Фильтр</InputLabel>
              <Select
                value={selectedFilter || ''}
                onChange={(e) => handleFilterChange(e.target.value)}
                label="Фильтр"
              >
                <MenuItem value=""><em>Нет</em></MenuItem>
                {filters.map(filter => (
                  <MenuItem key={filter.id} value={filter.id}>{filter.filter_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Инвертировать языки">
              <IconButton 
                  onClick={toggleInverted}
                  className={inverted ? "inverted-toggle" : ""}
                  sx={{ 
                  height: 56, 
                  width: 56,
                  marginLeft: 1,
                  }}
              >
                  <SwapHorizIcon 
                  className={inverted ? "inverted-icon inverted-icon-active" : "inverted-icon"}
                  />
              </IconButton>
            </Tooltip>
            <Button
              variant={reviewMode ? 'contained' : 'outlined'}
              onClick={toggleReviewMode}
              sx={{ minHeight: 56 }}
            >
              На повторение
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

        <Stack direction="row" sx={{ mb: 2, gap: 1 }} flexWrap="wrap" alignItems="center">
        {selectedTheme && (
            <Chip label={`Тема: ${themes.find(t => t.id === selectedTheme)?.name}`} onDelete={clearTheme} />
        )}
        {selectedFilter && (
            <Chip label={`Фильтр: ${filters.find(f => f.id === selectedFilter)?.filter_name}`} onDelete={clearFilter} />
        )}
        {reviewMode && (
            <Chip label="Режим: На повторение" color="warning" onDelete={toggleReviewMode} />
        )}
        {(selectedTheme || selectedFilter || reviewMode) && words.length > 0 && (
            <Chip label={`Пройдено: ${currentIndex} / ${words.length}`} color="success" />
        )}
        {(selectedTheme || selectedFilter || reviewMode) && words.length > 0 && mistakes > 0 && (
            <Chip label={`Ошибок: ${mistakes}`} color="error" />
        )}
        </Stack>


      {loading.words ? (
        <CircularProgress />
      ) : finished ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Поздравляем! Все слова пройдены.</Typography>
          <Button variant="contained" onClick={handleRepeat} sx={{ mt: 2 }}>
            Повторить еще раз
          </Button>
        </Box>
      ) : words.length > 0 && currentIndex < words.length && (
        <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>Слово:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Typography variant="h4">
              {inverted ? currentWord.foreign_word : currentWord.russian_word}
            </Typography>
            {inverted && (
              <Button
                variant="text"
                size="small"
                color="primary"
                onClick={() => speakWord(currentWord.foreign_word)}
                sx={{ ml: 1 }}
                aria-label="Произнести слово"
              >
                🔊
              </Button>
            )}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
            {!isCurrentWordInReview ? (
              <Button variant="outlined" onClick={handleAddCurrentWordToReview}>
                + В повторение
              </Button>
            ) : (
              <Button variant="outlined" color="warning" onClick={handleRemoveCurrentWordFromReview}>
                Убрать из повторения
              </Button>
            )}
          </Stack>

          <Box sx={{ position: 'relative' }}>
            <Stack spacing={2} sx={{ filter: showOptions ? 'none' : 'blur(8px)', transition: 'filter 0.3s', pointerEvents: showOptions ? 'auto' : 'none' }}>
              {options.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleAnswer(option)}
                    sx={{
                      flexGrow: 1,
                      '&:hover, &:focus': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {option}
                  </Button>
                  {!inverted && (
                    <Button
                      variant="text"
                      size="medium"
                      color="primary"
                      onClick={() => speakWord(option)}
                    >
                      🔊
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
            {!showOptions && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  zIndex: 2,
                }}
              >
                <Button variant="contained" onMouseDown={(e) => e.preventDefault()} onClick={() => {setShowOptions(true); setAccordionExpanded(false);}}>
                  Показать варианты ответов
                </Button>
              </Box>
            )}
          </Box>

          {feedback && (
            <Typography sx={{ mt: 2 }} color={feedback === 'Верно!' ? 'green' : 'red'}>
              {feedback}
            </Typography>
          )}
        </Paper>
      )}

      {!loading.words && words.length === 0 && reviewMode && (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          В наборе повторения пока нет слов.
        </Typography>
      )}
    </Box>
  );
};

export default CardsPage;
